import React, { Suspense, useEffect } from 'react';
import routes from './Routes';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import ProgressBars from '../UI/Progress/ProgressBar';

const KIOSK_ALLOWED_PATHS = ['/kioskentry', '/signinkiosk', '/signinkiosklocation'];

// Read-only, non-internal users are limited to list/overview screens: they can
// browse lists and open the row drawer, but not navigate into an individual
// record's detail screen. In-app record links are already rendered as plain text
// (see UI/Link), so this only guards against direct URL / bookmark access. A
// route is a blocked "detail" when it carries a record :id param (or is flagged
// detail:true), unless it is a print view or explicitly flagged allowReadOnly.
function isReadOnlyBlocked({ path, navMenu, detail, allowReadOnly }) {
  if (allowReadOnly === true) return false;
  if (detail === true) return true;
  const p = String(path || '');
  if (navMenu?.category === 'printView' || /print/i.test(p)) return false;
  return /\/:id(\/|$)/.test(p);
}

// v6 note: DynamicRoute is now rendered as the `element` of a <Route> in
// DynamicRouter (not as a wrapper that renders its own <Route>), since <Routes>
// requires its direct children to be <Route> elements. It no longer receives
// react-router's v5 match/location/history via a render-prop; downstream
// components that read `match.params` get it via the `match` prop rebuilt
// below from useParams(), matching the handful of legacy consumers.
function DynamicRoute({ auth, permission, dispatch, component: Component, path, navMenu, noPath, routeAuth, detail, allowReadOnly }) {
  const location = useLocation();
  const params = useParams();

  // RecordNavigation's prev/next arrows push a sibling record onto the same route (see
  // General/RecordNavigation.jsx), so only the :id param changes and react-router reuses the mounted
  // screen in place. That silently makes every detail screen responsible for resetting all of its own
  // state, and all of its children's, inside loadRecord. Nothing enforces it, and children holding
  // per-record state break it quietly: ActivityWorkRecord snapshots recordId at mount, so after
  // arrowing A -> B it would still post B's work record against parent A. Keying the screen by record
  // id (via useParams, which resolves against the matched route) hands that reset back to React.
  //
  // Creating a record needs no special case here. A screen's create and detail URLs are separate route
  // entries (/destruction and /destruction/:id), each rendering its own DynamicRoute keyed by path in
  // <Routes>, so history.replace('' -> new id) already swaps route entries and remounts. This only
  // changes behaviour when one entry stays matched across an id change, which is navigation. That
  // holds because every :id is a required path segment; an optional-param route would not match either
  // half of that reasoning and would need rethinking.
  const recordKey = params.id ?? '';

  useEffect(() => {
    dispatch({ type: "RESET_OPTIONMENU", payload: null });  // Reset option menu only on route navigation
    dispatch({ type: "CLEAR_ERROR", payload: null }); // Clear errors on route navigation
  }, [location.pathname, dispatch]);

  // Handle navigation state updates when route changes or permissions load
  useEffect(() => {
    if (routeAuth != null && auth.isAuthenticated === routeAuth.required && permission.read === true) {
      const crumbs = routes
        .filter(({ path }) => location.pathname.includes(path))
        .map(({ path, ...rest }) => ({
          path: Object.keys(rest.params || {}).length
            ? Object.keys(rest.params).reduce(
                (path, param) => path.replace(`:${param}`, rest.params[param]), path)
            : path,
          ...rest
        }));

      const crumbedNavMenu = { ...navMenu, crumbs };
      if (crumbedNavMenu.category !== 'printView') {
        dispatch({ type: "SET_NAVMENU_LOCATION", payload: crumbedNavMenu });
      }
    }
  }, [location.pathname, auth.isAuthenticated, permission.read, routeAuth, dispatch, navMenu]);

  // Keyed by record id so arrowing between records remounts the screen instead of reusing it.
  const screen = Component && (
    <Component key={recordKey} permission={permission} match={{ params, path, url: location.pathname }} location={location} />
  );

  if (routeAuth != null && auth.isAuthenticated === routeAuth.required) {
    // Internal-only routes require isInternalAdmin flag
    if (routeAuth.internal === true && auth.isInternalAdmin !== true) {
      return <Navigate to="/home" replace />;
    }
    // Internal admin users can only access internal routes
    if (routeAuth.internal === false && auth.isInternalAdmin === true) {
      return <Navigate to="/admin" replace />;
    }
          // Tenant-entitlement gated routes (e.g. flag: 'retail'). Bound to the
          // tenant's flag with no internal override — same rule as the API.
          if (routeAuth.flag && auth.user?.[routeAuth.flag] !== true) {
            return <Navigate to="/home" replace />;
          }
    // Kiosk users can only access kiosk routes - bypass permission check for allowed paths
    if (auth.user?.kiosk === true && !auth.user?.internal) {
      const isAllowed = KIOSK_ALLOWED_PATHS.some(p => location.pathname.startsWith(p));
      if (!isAllowed) {
        return <Navigate to="/kioskentry" replace />;
      }
      // Skip permission.read check for kiosk users on allowed paths
      return <Suspense fallback={<ProgressBars />}>{screen}</Suspense>;
    }
    if (permission.read !== true) {
      return <Navigate to="/home" replace />;
    }
    // Read-only, non-internal users cannot open record detail screens.
    if (auth.user?.readOnly === true && auth.user?.internal !== true &&
        isReadOnlyBlocked({ path, navMenu, detail, allowReadOnly })) {
      return <Navigate to="/home" replace />;
    }
    return <Suspense fallback={<ProgressBars />}>{screen}</Suspense>;
  } else if (routeAuth != null && !routeAuth.required) {
    return screen;
  } else if (noPath) {
    return <Navigate to="/home" state={{ referral: location.pathname }} replace />;
  } else {
    return <Navigate to="/login" state={{ referral: location.pathname }} replace />;
  }
}

export default React.memo(DynamicRoute);